<template>
  <ShortcutWrapper @n-shortcut="onNShortcut" isDialog>
    <DetailCard :title="$t('diameterGroups')" :style="{ borderRadius: '0px 4px 4px 4px' }">
        <template #title.append>
          <v-row
          v-if="crud"
          no-gutters
          justify="end">
            <Icon
            icon="mdi-plus"
            :tooltipText="$t('new')"
            @icon-clicked="newDiameterGroupClicked()"
            margin=""
            :small="false"
            tabindex="0"/>
          </v-row>
        </template>

        <v-data-table
        class="small-headers"
        :loading="loading"
        dense
        :items="accountDiameterGroups"
        :items-per-page="-1"
        hide-default-footer
        fixed-header
        :height="tableHeight"
        :headers="diameterGroupHeaders">

          <template #item.lowerBound="{ item }">
            <span v-if="item.lowerBound">{{ `${parseInt(item.lowerBound)}"` }}</span>
          </template>

          <template #item.upperBound="{ item }">
            <span v-if="item.upperBound">{{ `${parseInt(item.upperBound)}"` }}</span>
          </template>

          <template #item.actions="{ item }">
            <v-row v-if="!!item.label" no-gutters justify="end">
              <Icon
              icon="mdi-pencil"
              :tooltipText="$t('edit')"
              @icon-clicked="editDiameterGroupClicked(item)"/>
              <Icon
              icon="mdi-delete-forever"
              :tooltipText="$t('delete')"
              @icon-clicked="deleteDiameterGroupClicked(item)"
              margin=""/>
            </v-row>
          </template>
        </v-data-table>

      <Dialog
      :stateId="dialogId"
      @dialog-closing="handleCancelEdit"
      @click:outside="handleCancelEdit"
      @close="handleCancelEdit">
        <AccountDiameterGroupForm
        :diameterGroup="focusedDiameterGroup"
        @save="handleSaveDiameterGroup"
        @cancel="handleCancelEdit"/>
      </Dialog>
    </DetailCard>
  </ShortcutWrapper>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { diameterGroupHeaders } from '@/headers/Account'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'

export default {
  name: 'AccountDiameterGroups',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    AccountDiameterGroupForm: () => import('./AccountDiameterGroupForm.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },

    accountId: {
      type: Number,
      required: false,
      default: undefined
    }
  },

  async mounted () {
    await this.refresh()
  },

  data: () => ({
    rules: fieldRules.rules,
    dialogId: uniqueDialogId('account-diameter-group'),
    loading: true,
    focusedDiameterGroup: null
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountDiameterGroups']),
    ...mapGetters('dialog', ['topDialogState', 'dialogStateById']),

    tableHeight () {
      if (!this.topDialogState) return 'auto'
      const { fullscreen } = this.topDialogState
      const dialogHeightPcnt = fullscreen ? 100 : 90
      const dialogBufferSpace = 350
      return `max(${256}px, min(${800}px, calc(${dialogHeightPcnt}vh - ${dialogBufferSpace}px)))`
    },

    diameterGroupHeaders () {
      return diameterGroupHeaders(this.crud)
    }
  },

  methods: {
    ...mapActions('account-scaling', ['fetchAccountDiameterGroups', 'createAccountDiameterGroup',
      'updateAccountDiameterGroup', 'deleteAccountDiameterGroup']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async refresh () {
      this.loading = true
      if (this.accountId) {
        await this.fetchDiameterGroups()
      }
      this.loading = false
    },

    async fetchDiameterGroups () {
      await this.fetchAccountDiameterGroups(this.accountId)
    },

    async deleteDiameterGroupClicked (diameterGroup) {
      await this.deleteDiameterGroupOnServer(diameterGroup)
      await this.fetchDiameterGroups()
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.SimpleWide })
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    newDiameterGroupClicked () {
      this.openDialog()
    },

    editDiameterGroupClicked (diameterGroup) {
      this.focusedDiameterGroup = diameterGroup
      this.openDialog()
    },

    handleCancelEdit () {
      this.focusedDiameterGroup = null
      this.closeDialog()
    },

    async handleSaveDiameterGroup (diameterGroup) {
      if (diameterGroup.diameterGroupId) {
        await this.updateDiameterGroupOnServer(diameterGroup)
      } else {
        await this.createDiameterGroupOnServer(diameterGroup)
      }
      this.focusedDiameterGroup = null
      await this.fetchDiameterGroups()
      this.closeDialog()
    },

    async createDiameterGroupOnServer (diameterGroup) {
      const result = await this.createAccountDiameterGroup({
        accountId: this.accountId,
        ...diameterGroup
      })
      this.emitDiameterGroupMutated()
      return result
    },

    async updateDiameterGroupOnServer (diameterGroup) {
      const result = await this.updateAccountDiameterGroup({
        accountId: this.accountId,
        ...diameterGroup
      })
      this.emitDiameterGroupMutated()
      return result
    },

    async deleteDiameterGroupOnServer (diameterGroup) {
      const result = await this.deleteAccountDiameterGroup({
        accountId: this.accountId,
        diameterGroupId: diameterGroup.diameterGroupId
      })
      this.emitDiameterGroupMutated()
      return result
    },

    onNShortcut () {
      if (this.childDialogIsOpen()) {
        return
      }
      this.newDiameterGroupClicked()
    },

    childDialogIsOpen () {
      return !!this.dialogStateById(this.dialogId)
    },

    emitDiameterGroupMutated () {
      this.$emit('account-diameter-group-mutated')
    }
  }
}
</script>
